import clsx from 'clsx';
import { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { SharedLinkProps } from '../../types/SharedLinkProps';

import { useLinkElement } from '../../context/LinkNavigation/LinkNavigation';
import { Icon } from '../Icon/Icon';
import { Skeleton } from '../Skeleton/Skeleton';
import { Text } from '../Text/Text';
import './Link.css';

const messages = defineMessages({
  externalLinkIconLabel: {
    id: 'Link.launchTooltip',
    defaultMessage: 'Opens in a new tab',
    description: 'Tooltip for launch icon indicating link opens in a new tab',
  },
});

export type LinkProps =
  | (SharedLinkProps & { isLoading?: false })
  | {
      /** Replaces the link with a skeleton bar matching its `size` while its value loads. */
      isLoading: true;
      size?: SharedLinkProps['size'];
      external?: never;
      href?: never;
      text?: never;
      truncate?: never;
      variant?: never;
    };

function LinkAnchor(props: SharedLinkProps) {
  const intl = useIntl();
  const externalLinkIconLabel = intl.formatMessage(
    messages.externalLinkIconLabel,
  );
  const {
    external = false,
    href,
    size = 'm',
    text,
    truncate,
    variant = 'default',
  } = props;

  // External links stay native anchors; in-app links route through the app.
  const LinkElement = useLinkElement(href, external ? '_blank' : undefined);

  return (
    <LinkElement
      className={clsx(
        'eds-link',
        `eds-link--variant-${variant}`,
        `eds-link--${size}`,
        external && 'eds-link--external',
        truncate && 'eds-link--truncate',
      )}
      href={href}
      rel={external ? 'noopener noreferrer' : undefined}
      target={external ? '_blank' : undefined}
    >
      <Text truncate={truncate} __private={{ inInteractive: true }}>
        {text}
      </Text>
      {external && (
        <span className="eds-link--icon">
          <Icon
            icon="open-external-link"
            label={externalLinkIconLabel}
            size="s"
          />
        </span>
      )}
    </LinkElement>
  );
}

export const Link = memo((props: LinkProps) => {
  if (props.isLoading) {
    return <Skeleton size={props.size ?? 'm'} />;
  }

  return <LinkAnchor {...props} />;
});

Link.displayName = 'Link';
